import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  BottomSheet,
  Button,
  Chip,
  Notice,
  Segmented,
  TextField,
  useSnackbar,
  DateField,
} from '@sauda/ui/kit';
import { useAuth } from '../../auth/AuthContext.js';
import { ApiError, api } from '../../lib/api.js';
import type { DealDetailView } from '../../lib/types.js';
import { formatRupees, paiseToRupeesInput, rupeesToPaise } from '../../lib/money.js';
import { istDateInput, otherShare, splitAddsUp } from '../../lib/money-input.js';

const MODES = ['upi', 'cash', 'bank', 'cheque'] as const;
type PaidBy = 'tenant' | 'owner' | 'both';

/** What the split looks like, to pre-select who pays. */
function paidByOf(split: { tenantPaise: number; ownerPaise: number } | null): PaidBy {
  if (split === null) return 'both';
  if (split.ownerPaise === 0) return 'tenant';
  if (split.tenantPaise === 0) return 'owner';
  return 'both';
}

const draftKey = (dealId: string): string => `sauda.draft.terms.${dealId}`;
type Draft = Record<string, unknown>;
function readDraft(dealId: string): Draft | null {
  try {
    const raw = window.sessionStorage.getItem(draftKey(dealId));
    return raw === null ? null : (JSON.parse(raw) as Draft);
  } catch {
    return null;
  }
}
function writeDraft(dealId: string, draft: Draft | null): void {
  try {
    if (draft === null) window.sessionStorage.removeItem(draftKey(dealId));
    else window.sessionStorage.setItem(draftKey(dealId), JSON.stringify(draft));
  } catch {
    /* a private window keeps the draft only while the sheet stays mounted */
  }
}

/**
 * Token and terms in one save: the token, the owner's details and the brokerage with who pays.
 * On an open deal the same save records the token, which moves the deal to its next stage.
 */
export function TermsSheet({
  deal,
  open,
  onClose,
  memoOut,
}: {
  deal: DealDetailView;
  open: boolean;
  onClose: () => void;
  memoOut: boolean;
}) {
  const { t } = useTranslation();
  const auth = useAuth();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const [amount, setAmount] = useState('');
  const [paidTo, setPaidTo] = useState('');
  const [mode, setMode] = useState<(typeof MODES)[number] | null>(null);
  const [date, setDate] = useState(istDateInput());
  const [ownerName, setOwnerName] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [total, setTotal] = useState('');
  const [paidBy, setPaidBy] = useState<PaidBy>('both');
  const [tenant, setTenant] = useState('');
  const [owner, setOwner] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [rera, setRera] = useState('');
  const [incentiveType, setIncentiveType] = useState<'none' | 'percent' | 'fixed'>('none');
  const [incentive, setIncentive] = useState('');
  const [ownerBacks, setOwnerBacks] = useState('');
  const [tenantBacks, setTenantBacks] = useState('');
  const [returnedBy, setReturnedBy] = useState('');
  const [occupation, setOccupation] = useState('');
  const [idSeen, setIdSeen] = useState<'' | 'yes' | 'no'>('');
  const [people, setPeople] = useState('');
  const [depAmount, setDepAmount] = useState('');
  const [depPaidTo, setDepPaidTo] = useState('');
  const [depDate, setDepDate] = useState('');
  const [refundTerms, setRefundTerms] = useState('');
  const [error, setError] = useState<string | null>(null);

  // What the person typed so far, kept when the sheet is closed by accident and until it is saved.
  const draftNow = (): Draft => ({
    amount,
    paidTo,
    mode,
    date,
    ownerName,
    ownerPhone,
    total,
    paidBy,
    tenant,
    owner,
    dueOn,
    rera,
    incentiveType,
    incentive,
    ownerBacks,
    tenantBacks,
    returnedBy,
    occupation,
    idSeen,
    people,
    depAmount,
    depPaidTo,
    depDate,
    refundTerms,
  });
  const close = (): void => {
    writeDraft(deal.id, draftNow());
    onClose();
  };

  // Start from the person's unsaved draft if there is one, else from what the deal already holds.
  useEffect(() => {
    if (!open) return;
    const saved = readDraft(deal.id);
    if (saved !== null) {
      setAmount(saved.amount as never);
      setPaidTo(saved.paidTo as never);
      setMode(saved.mode as never);
      setDate(saved.date as never);
      setOwnerName(saved.ownerName as never);
      setOwnerPhone(saved.ownerPhone as never);
      setTotal(saved.total as never);
      setPaidBy(saved.paidBy as never);
      setTenant(saved.tenant as never);
      setOwner(saved.owner as never);
      setDueOn(saved.dueOn as never);
      setRera(saved.rera as never);
      setIncentiveType(saved.incentiveType as never);
      setIncentive(saved.incentive as never);
      setOwnerBacks(saved.ownerBacks as never);
      setTenantBacks(saved.tenantBacks as never);
      setReturnedBy(saved.returnedBy as never);
      setOccupation(saved.occupation as never);
      setIdSeen(saved.idSeen as never);
      setPeople(saved.people as never);
      setDepAmount(saved.depAmount as never);
      setDepPaidTo(saved.depPaidTo as never);
      setDepDate(saved.depDate as never);
      setRefundTerms(saved.refundTerms as never);
      setError(null);
      return;
    }
    setAmount(paiseToRupeesInput(deal.token.amountPaise));
    setPaidTo(deal.token.paidTo ?? deal.owner.name ?? '');
    setMode(
      (MODES as readonly string[]).includes(deal.token.mode ?? '')
        ? (deal.token.mode as (typeof MODES)[number])
        : null,
    );
    setDate(deal.token.date ?? istDateInput());
    setOwnerName(deal.owner.name ?? '');
    setOwnerPhone(deal.owner.phone ?? '');
    setTotal(paiseToRupeesInput(deal.brokerage.totalPaise));
    setPaidBy(paidByOf(deal.brokerage.split));
    setTenant(paiseToRupeesInput(deal.brokerage.split?.tenantPaise ?? null));
    setOwner(paiseToRupeesInput(deal.brokerage.split?.ownerPaise ?? null));
    setDueOn(deal.brokerage.dueOn ?? '');
    const facts = deal.facts ?? {};
    setOwnerBacks(facts.tokenTerms?.ownerBacksOut ?? '');
    setTenantBacks(facts.tokenTerms?.tenantBacksOut ?? '');
    setReturnedBy(facts.tokenTerms?.returnedBy ?? '');
    setOccupation(facts.tenant?.occupation ?? '');
    setIdSeen(facts.tenant?.idSeen ?? '');
    setPeople(facts.tenant?.people == null ? '' : String(facts.tenant.people));
    setDepAmount(paiseToRupeesInput(facts.deposit?.amountPaise ?? null));
    setDepPaidTo(facts.deposit?.paidTo ?? '');
    setDepDate(facts.deposit?.date ?? '');
    setRefundTerms(facts.deposit?.refundTerms ?? '');
    setRera(facts.reraNumber ?? '');
    setError(null);
    // Re-seeded only when the sheet opens, never while the person types.
  }, [open]);

  const split = (): { tenantPaise: number; ownerPaise: number } | null => {
    const totalPaise = rupeesToPaise(total);
    if (totalPaise === null) return null;
    if (paidBy === 'tenant') return { tenantPaise: totalPaise, ownerPaise: 0 };
    if (paidBy === 'owner') return { tenantPaise: 0, ownerPaise: totalPaise };
    const a = rupeesToPaise(tenant);
    const b = rupeesToPaise(owner);
    return a === null || b === null ? null : { tenantPaise: a, ownerPaise: b };
  };
  // A token amount without a chosen mode is not saved: the mode is the broker's report, never a guess.
  const tokenNeedsMode = (rupeesToPaise(amount) ?? 0) > 0 && mode === null;
  const splitOk =
    total.trim() === '' || (paidBy === 'both' ? splitAddsUp(total, tenant, owner) : true);

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {};
      const tokenPaise = rupeesToPaise(amount);
      if (tokenPaise !== null && tokenPaise > 0 && paidTo.trim() !== '' && mode !== null)
        body['token'] = { amountPaise: tokenPaise, paidTo: paidTo.trim(), mode, date };
      const shares = split();
      const totalPaise = rupeesToPaise(total);
      if (totalPaise !== null && shares !== null) {
        const pct = rupeesToPaise(incentive);
        body['brokerage'] = {
          totalPaise,
          split: shares,
          dueOn: dueOn === '' ? null : dueOn,
          ...(auth.allowed('records.reassign') && incentiveType !== 'none' && pct !== null
            ? {
                incentive:
                  incentiveType === 'percent'
                    ? { type: 'percent', bps: Math.round(pct) }
                    : { type: 'fixed', paise: pct },
              }
            : {}),
        };
      }
      if (ownerName.trim() !== '' && ownerName.trim() !== (deal.owner.name ?? ''))
        body['ownerPartyName'] = ownerName.trim();
      if (ownerPhone.trim() !== '' && ownerPhone.trim() !== (deal.owner.phone ?? ''))
        body['ownerPartyPhone'] = ownerPhone.trim();
      const facts: Record<string, unknown> = {};
      const termsGiven = [ownerBacks, tenantBacks, returnedBy].filter(
        (v) => v.trim() !== '',
      ).length;
      if (termsGiven === 3) {
        facts['tokenTerms'] = {
          ownerBacksOut: ownerBacks.trim(),
          tenantBacksOut: tenantBacks.trim(),
          returnedBy,
        };
      }
      if (occupation.trim() !== '' || idSeen !== '' || people.trim() !== '') {
        facts['tenant'] = {
          occupation: occupation.trim() === '' ? null : occupation.trim(),
          idSeen: idSeen === '' ? null : idSeen,
          people: people.trim() === '' ? null : Number(people),
        };
      }
      if (depAmount.trim() !== '' || depPaidTo.trim() !== '' || refundTerms.trim() !== '') {
        facts['deposit'] = {
          amountPaise: rupeesToPaise(depAmount),
          paidTo: depPaidTo.trim() === '' ? null : depPaidTo.trim(),
          date: depDate === '' ? null : depDate,
          refundTerms: refundTerms.trim() === '' ? null : refundTerms.trim(),
        };
      }
      if (deal.kind === 'sale' && rera.trim() !== '') facts['reraNumber'] = rera.trim();
      if (Object.keys(facts).length > 0) body['facts'] = facts;
      if (Object.keys(body).length === 0) throw new Error('empty');
      await api(`/deals/${deal.id}/terms`, { method: 'PATCH', body });
      // The terms are saved even if the step cannot be taken, and the message says which happened.
      if (deal.state === 'open' && body['token'] !== undefined) {
        try {
          await api(`/deals/${deal.id}/transition`, {
            method: 'POST',
            body: { to: 'token_recorded' },
          });
        } catch (e) {
          throw new Error(`step:${e instanceof ApiError ? e.message : ''}`);
        }
        return 'token' as const;
      }
      return 'terms' as const;
    },
    onSuccess: async (kind) => {
      await Promise.all([
        cache.invalidateQueries({ queryKey: ['deal', deal.id] }),
        cache.invalidateQueries({ queryKey: ['deals'] }),
        cache.invalidateQueries({ queryKey: ['today'] }),
      ]);
      writeDraft(deal.id, null);
      onClose();
      snack(
        kind === 'token'
          ? { text: t('deal.tokenRecorded', { amount: formatRupees(rupeesToPaise(amount) ?? 0) }) }
          : { text: t('deal.termsSaved') },
      );
    },
    onError: async (e) => {
      await cache.invalidateQueries({ queryKey: ['deal', deal.id] });
      if (e instanceof Error && e.message.startsWith('step:'))
        setError(t('deal.savedNotStepped', { reason: e.message.slice(5) }));
      else if (e instanceof Error && e.message === 'empty') setError(t('deal.nothingToSave'));
      else
        setError(
          e instanceof ApiError && e.message !== '' && e.status < 500
            ? e.message
            : t('deal.generic'),
        );
    },
  });

  return (
    <BottomSheet
      open={open}
      onClose={close}
      label={t('deal.termsTitle')}
      title={t('deal.termsTitle')}
      closeLabel={t('app.close')}
      actions={
        <Button
          size="lg"
          block
          loading={save.isPending}
          disabled={!splitOk || tokenNeedsMode}
          onClick={() => save.mutate()}
        >
          {deal.state === 'open' ? t('deal.saveAndRecord') : t('app.save')}
        </Button>
      }
    >
      <div className="k-stack">
        {memoOut ? <Notice tone="warning">{t('deal.memoOutWarning')}</Notice> : null}
        <TextField
          label={t('deal.tokenAmount')}
          money
          prefix="₹"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <TextField
          label={t('deal.paidTo')}
          value={paidTo}
          onChange={(e) => setPaidTo(e.target.value)}
        />
        <div className="chips-row" role="group" aria-label={t('deal.tokenMode')}>
          {MODES.map((m) => (
            <Chip key={m} selected={mode === m} onClick={() => setMode(m)}>
              {t(`deals.mode_${m}`)}
            </Chip>
          ))}
        </div>
        {tokenNeedsMode ? (
          <p className="k-t-body-sm k-muted" role="status">
            {t('deal.tokenModeRequired')}
          </p>
        ) : null}
        <DateField label={t('deal.tokenDate')} value={date} onChange={setDate} />
        <TextField
          label={t('deal.ownerName')}
          value={ownerName}
          onChange={(e) => setOwnerName(e.target.value)}
        />
        <TextField
          label={t('deal.ownerPhone')}
          type="tel"
          inputMode="numeric"
          prefix="+91"
          value={ownerPhone.replace(/^\+91/, '')}
          onChange={(e) => setOwnerPhone(e.target.value)}
        />
        <TextField
          label={t('deal.brokerageTotal')}
          money
          prefix="₹"
          inputMode="decimal"
          value={total}
          onChange={(e) => setTotal(e.target.value)}
        />
        <span className="k-t-label-lg">{t('deal.whoPays')}</span>
        <Segmented
          label={t('deal.whoPays')}
          value={paidBy}
          onChange={(v) => setPaidBy(v as PaidBy)}
          options={[
            { value: 'tenant', label: t('deals.party_tenant') },
            { value: 'owner', label: t('deals.party_owner') },
            { value: 'both', label: t('deal.both') },
          ]}
        />
        {paidBy === 'both' ? (
          <div className="k-pair">
            <TextField
              label={t('deal.tenantPays')}
              inputMode="decimal"
              prefix="₹"
              value={tenant}
              onChange={(e) => {
                setTenant(e.target.value);
                setOwner(otherShare(total, e.target.value));
              }}
              error={splitOk ? undefined : t('deal.splitMismatch')}
            />
            <TextField
              label={t('deal.ownerPays')}
              inputMode="decimal"
              prefix="₹"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
            />
          </div>
        ) : null}
        <DateField label={t('deal.dueOn')} value={dueOn} onChange={setDueOn} />
        {auth.allowed('records.reassign') ? (
          <>
            <span className="k-t-label-lg">{t('deal.incentive')}</span>
            <Segmented
              label={t('deal.incentive')}
              value={incentiveType}
              onChange={(v) => setIncentiveType(v as 'none' | 'percent' | 'fixed')}
              options={[
                { value: 'none', label: t('deal.incentiveNone') },
                { value: 'percent', label: t('deal.incentivePercent') },
                { value: 'fixed', label: t('deal.incentiveFixed') },
              ]}
            />
            {incentiveType === 'none' ? null : (
              <TextField
                label={
                  incentiveType === 'percent'
                    ? t('deal.incentivePercentValue')
                    : t('deal.incentiveFixedValue')
                }
                inputMode="decimal"
                value={incentive}
                onChange={(e) => setIncentive(e.target.value)}
              />
            )}
          </>
        ) : null}
        <h3 className="k-t-title-sm">{t('deal.tokenTermsTitle')}</h3>
        <p className="k-t-body-sm k-muted">{t('deal.tokenTermsHelp')}</p>
        <TextField
          label={t('deal.ownerBacksOut')}
          supporting={t('deal.ownerBacksHint')}
          value={ownerBacks}
          onChange={(e) => setOwnerBacks(e.target.value)}
        />
        <TextField
          label={t('deal.tenantBacksOut')}
          supporting={t('deal.tenantBacksHint')}
          value={tenantBacks}
          onChange={(e) => setTenantBacks(e.target.value)}
        />
        <DateField label={t('deal.returnedBy')} value={returnedBy} onChange={setReturnedBy} />
        <h3 className="k-t-title-sm">{t('deal.tenantFactsTitle')}</h3>
        <TextField
          label={t('deal.occupation')}
          supporting={t('deal.occupationHint')}
          value={occupation}
          onChange={(e) => setOccupation(e.target.value)}
        />
        <span className="k-t-label-lg">{t('deal.idSeen')}</span>
        <Segmented
          label={t('deal.idSeen')}
          value={idSeen}
          onChange={(v) => setIdSeen(v as '' | 'yes' | 'no')}
          options={[
            { value: '', label: t('listing.unsaid') },
            { value: 'yes', label: t('listing.yes') },
            { value: 'no', label: t('listing.no') },
          ]}
        />
        <TextField
          label={t('deal.people')}
          supporting={t('deal.peopleHint')}
          inputMode="numeric"
          value={people}
          onChange={(e) => setPeople(e.target.value.replace(/\D/g, ''))}
        />
        <h3 className="k-t-title-sm">{t('deal.depositTitle')}</h3>
        <div className="form-pair">
          <TextField
            label={t('deal.depositAmount')}
            prefix="₹"
            inputMode="decimal"
            value={depAmount}
            onChange={(e) => setDepAmount(e.target.value)}
          />
          <TextField
            label={t('deal.depositPaidTo')}
            supporting={t('deal.depositPaidToHint')}
            value={depPaidTo}
            onChange={(e) => setDepPaidTo(e.target.value)}
          />
        </div>
        <DateField label={t('deal.depositDate')} value={depDate} onChange={setDepDate} />
        <TextField
          label={t('deal.refundTerms')}
          supporting={t('deal.refundHint')}
          value={refundTerms}
          onChange={(e) => setRefundTerms(e.target.value)}
        />
        {deal.kind === 'sale' ? (
          <>
            <TextField
              label={t('deal.reraNumber')}
              value={rera}
              onChange={(e) => setRera(e.target.value)}
            />
            <p className="k-t-body-sm k-muted">{t('deal.saleNote')}</p>
          </>
        ) : null}
        {error === null ? null : <Notice tone="error">{error}</Notice>}
      </div>
    </BottomSheet>
  );
}
